import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { usePageTitle } from "@/lib/usePageTitle";
import { CheckCircle2, AlertCircle, Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NoIndex = () => {
  useEffect(() => {
    // Swap the global SEO meta robots with a noindex directive while this page is mounted.
    const existing = document.querySelector("meta[name='robots']");
    const prevContent = existing ? existing.getAttribute("content") : null;
    if (existing) {
      existing.setAttribute("content", "noindex, nofollow");
    } else {
      const meta = document.createElement("meta");
      meta.name = "robots";
      meta.content = "noindex, nofollow";
      meta.dataset.unsubscribeInjected = "true";
      document.head.appendChild(meta);
    }
    return () => {
      if (existing && prevContent != null) {
        existing.setAttribute("content", prevContent);
      } else {
        const injected = document.querySelector(
          "meta[name='robots'][data-unsubscribe-injected='true']",
        );
        if (injected) injected.remove();
      }
    };
  }, []);
  return null;
};

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [email, setEmail] = useState("");
  const [cancelled, setCancelled] = useState(0);
  usePageTitle("Cancelar inscrição · Na Ponta Da Língua Brasil");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }
    let cancelledLocal = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/nurture/unsubscribe?token=${encodeURIComponent(token)}`,
        );
        if (cancelledLocal) return;
        setEmail(data.email || "");
        setCancelled(data.cancelled_pending || 0);
        setStatus("ok");
      } catch {
        if (cancelledLocal) return;
        setStatus("error");
      }
    })();
    return () => {
      cancelledLocal = true;
    };
  }, [token]);

  return (
    <div
      data-testid="unsubscribe-page"
      className="min-h-screen bg-[#f4f1ea] text-[#1e2a5a] flex items-center justify-center px-6"
    >
      {/* Tell crawlers not to index this page */}
      <NoIndex />
      <div className="w-full max-w-md text-center">
        {status === "loading" && (
          <div className="inline-flex items-center gap-3 text-[#5c5c5c]">
            <Loader2 className="h-5 w-5 animate-spin" /> Processando…
          </div>
        )}

        {status === "ok" && (
          <>
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#1e7a3e]/10 text-[#1e7a3e] mb-6">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <p className="overline">Tudo certo</p>
            <h1 className="font-display mt-4 text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              Você foi descadastrado.
            </h1>
            {email && (
              <p className="mt-5 text-sm text-[#5c5c5c]">
                Endereço removido: <strong className="text-[#1e2a5a]">{email}</strong>
              </p>
            )}
            {cancelled > 0 && (
              <p className="mt-3 text-sm text-[#5c5c5c]">
                {cancelled} {cancelled === 1 ? "e-mail futuro foi cancelado" : "e-mails futuros foram cancelados"}.
              </p>
            )}
            <p className="mt-8 text-base text-[#3a3a3a] leading-relaxed">
              Você não vai mais receber a sequência de dicas do Prof Lionel.
              Se um dia quiser voltar, é só preencher qualquer formulário no site
              novamente — ou nos chamar no WhatsApp.
            </p>
            <Link
              to="/"
              data-testid="unsubscribe-back"
              className="group mt-10 inline-flex items-center gap-2 rounded-full border border-[#1e2a5a]/30 px-6 py-3 text-sm text-[#1e2a5a] hover:border-[#1e2a5a] transition-colors"
            >
              Voltar ao site{" "}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#d9381e]/10 text-[#d9381e] mb-6">
              <AlertCircle className="h-8 w-8" />
            </div>
            <p className="overline">Link inválido</p>
            <h1 className="font-display mt-4 text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              Não conseguimos processar.
            </h1>
            <p className="mt-6 text-base text-[#5c5c5c] leading-relaxed">
              Esse link de descadastramento parece estar inválido ou já foi usado.
              Se mesmo assim você continuar recebendo e-mails, responda qualquer
              um deles ou escreva direto para{" "}
              <a
                href="mailto:lionel@npdlbr.com.br"
                className="link-underline text-[#1e2a5a]"
              >
                lionel@npdlbr.com.br
              </a>{" "}
              — a gente resolve manualmente.
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 text-sm link-underline"
            >
              ← Voltar ao site
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
